/**
 * One labelled figure in a strip of them.
 *
 * Four pages had grown their own copy of this (Stat, Stat, Stat, Kpi), each a
 * Card with the same three lines. One component is what makes the strips read
 * as one product, and it is where the Atelier numeral treatment lives:
 * Montserrat, 26px, tabular, with the label above and the caveat below.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { InfoHint } from "./info-hint";

export function KpiTile({
  label,
  value,
  hint,
  help,
  tone = "default",
  spark,
  className,
}: {
  label: string;
  value: ReactNode;
  /** The caveat or context line. Omit rather than pad. */
  hint?: ReactNode;
  /** The derivation, shown as a tooltip on the label. */
  help?: string;
  /** `bad` colours the figure. Used for a count that should be zero. */
  tone?: "default" | "bad";
  /** An optional sparkline under the hint. */
  spark?: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="kpi-tile"
      className={cn(
        "flex min-w-0 flex-col rounded-lg border bg-card px-[18px] py-4 text-card-foreground shadow-sm",
        className,
      )}
    >
      <span
        data-kpi-label
        title={help}
        className="flex items-center gap-1.5 font-display text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
      >
        {label}
        {help ? <InfoHint text={help} data-kpi-help className="[&_svg]:size-3" /> : null}
      </span>
      <span
        data-kpi-value
        data-tone={tone}
        className={cn(
          "mt-2 font-display text-[26px] font-semibold leading-none tracking-tight tabular-nums",
          tone === "bad" && "text-destructive",
        )}
      >
        {value}
      </span>
      {hint ? (
        <span data-kpi-hint className="mt-1.5 text-xs text-muted-foreground">
          {hint}
        </span>
      ) : null}
      {spark ? <div className="mt-2.5 h-7">{spark}</div> : null}
    </div>
  );
}
